import {
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

export interface ScaledNumber {
    value: number;
    scale: number;
}

let allowedCharacters: Set<string>;
let allowedFixedCharacters: Set<string>;

(function () {
    const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    allowedCharacters = new Set([...numbers, '.']);
    allowedFixedCharacters = new Set(numbers);
})();

@Directive({
    selector: 'input[type="text"][scaledNumericInput]',
    standalone: false
})
export class ScaledNumericInputDirective implements OnChanges {
    @Output() onScaledInput = new EventEmitter<ScaledNumber>();

    @Output() scaleValueChange = new EventEmitter<number>();
    @Input() scaleValue: number = 0;
    @Input() scale: number = 1;
    @Input() fixedScale: boolean = false;

    constructor(public element: ElementRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (this.scaleValue == null) {
            this.element.nativeElement.value = null;
            return;
        }
        if (Number.isNaN(this.scaleValue)) return;

        if (this.scaleValue > Math.floor(this.scaleValue)) {
            throw new Error('decimal values cannot be passed to scaleValue');
        }

        const decimalPlaces = Math.log10(this.scale);
        const pointerLocation = this.getPointerLocation();
        let valueString = '' + this.scaleValue;
        let negative = false;

        if (valueString.startsWith('-')) {
            negative = true;
            valueString = valueString.substring(1);
        }

        if (valueString.length <= decimalPlaces) {
            const leadingZeros = decimalPlaces - valueString.length;

            for (let i = 0; i < leadingZeros; i++) {
                valueString = '0' + valueString;
            }

            valueString = '0.' + valueString;
        } else if (decimalPlaces > 0) {
            const beforeDecimal = valueString.slice(0, valueString.length - decimalPlaces);
            const afterDecimal = valueString.slice(valueString.length - decimalPlaces);
            valueString = beforeDecimal + '.' + afterDecimal;
        }

        if (negative) {
            valueString = '-' + valueString;
        }

        this.element.nativeElement.value = valueString;
        this.setPointerLocation(pointerLocation);
    }

    @HostListener('input')
    onInput() {
        this.emitValue();
    }

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        const isInput = event.key.length == 1;
        const decimalLocation = this.element.nativeElement.value.indexOf('.');
        const pointerLocation = this.getPointerLocation();
        const validCharacters =
            this.fixedScale || decimalLocation != -1 ? allowedFixedCharacters : allowedCharacters;

        if (
            event.key === '-' &&
            this.element.nativeElement.value[0] !== '-' &&
            pointerLocation === 0
        ) {
            return;
        }

        if (isInput && !validCharacters.has(event.key)) {
            event.preventDefault();
            return;
        }

        if (this.fixedScale) {
            const isTypingDecimal = decimalLocation != -1 && decimalLocation < pointerLocation;

            //Don't allow inserting the decimal for fixed scale inputs
            // the directive will insert the decimal
            if (this.fixedScale && event.key === '.') {
                event.preventDefault();
                return;
            }
            //Value deleted from the right of the pointer
            else if (event.key == 'Delete') {
                //TODO don't allow deleting the decimal
                if (this.element.nativeElement.value[pointerLocation] !== '.') {
                    this.replaceTextboxValue(pointerLocation, '0');
                    this.setPointerLocation(pointerLocation);
                }
                event.preventDefault();
                this.emitValue();
            } else if (isTypingDecimal) {
                //A value was inserted into the textbox, and the pointer isn't at the end of the input
                if (isInput) {
                    if (pointerLocation != this.element.nativeElement.value.length) {
                        this.replaceTextboxValue(pointerLocation, event.key);
                        this.setPointerLocation(pointerLocation + 1);
                    }

                    event.preventDefault();
                    this.emitValue();
                }
                //Value deleted from the left of the pointer
                else if (event.key == 'Backspace') {
                    //TODO don't allow deleting the decimal
                    if (this.element.nativeElement.value[pointerLocation - 1] !== '.') {
                        this.replaceTextboxValue(pointerLocation - 1, '0');
                        this.setPointerLocation(pointerLocation - 1);
                    } else {
                        this.setPointerLocation(pointerLocation - 1);
                    }
                    event.preventDefault();
                    this.emitValue();
                }
            }
        }
    }

    emitValue() {
        const input = this.element.nativeElement.value;
        const decimalCount = this.countDecimals(input);
        const decimalIndex = input.indexOf('.');

        let scale = Math.pow(10, decimalCount);
        const value =
            decimalCount < 2 && input.trim() !== '' ? Number(input.replace('.', '')) : NaN;

        if (decimalIndex !== -1) {
            scale = Math.pow(10, input.length - 1 - decimalIndex);
        }

        this.scaleValueChange.emit(value);
        this.onScaledInput.emit({
            value,
            scale,
        });
    }

    replaceTextboxValue(index: number, replacementValue: string | number) {
        let value: string = this.element.nativeElement.value;
        value =
            value.substring(0, index) + replacementValue + value.substring(index + 1, value.length);
        this.element.nativeElement.value = value;
    }

    getPointerLocation() {
        return this.element.nativeElement.selectionStart;
    }

    setPointerLocation(index: number) {
        this.element.nativeElement.setSelectionRange(index, index);
    }

    countDecimals(input: string) {
        let decimals = 0;

        for (let i = 0; i < input.length; i++) {
            if (input[i] == '.') {
                decimals++;
            }
        }

        return decimals;
    }
}
